import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { Center, Spinner, Alert, AlertIcon } from '@chakra-ui/react'
import FilePreview from '@/components/FilePreview'
import { FileItem } from '@/types'
import apiService from '@/services/api'
import { useNavigation } from '@/hooks/useNavigation'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { findFolderById } from '@/utils/folderUtils'
import { folderPathToHash } from '@/utils/hashNavigation'

export default function FilePreviewContent() {
  const { fileId } = useParams<{ fileId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { selectFile, folderTree } = useNavigation()
  const [file, setFile] = useState<FileItem | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { t } = useAppTranslation(['errors'])

  useEffect(() => {
    const loadFile = async () => {
      if (!fileId) {
        setError(t('filePreview.noFileId', { ns: 'errors' }))
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        const fileData = await apiService.getFile(parseInt(fileId))
        setFile(fileData)
        selectFile(fileData)
      } catch (err: any) {
        console.error('Failed to load file:', err)
        setError(err.response?.data?.error || t('filePreview.loadFailed', { ns: 'errors' }))
      } finally {
        setIsLoading(false)
      }
    }

    loadFile()
  }, [fileId, t])

  const handleBack = () => {
    const fromParam = searchParams.get('from')
    selectFile(null)

    if (fromParam === 'search') {
      // Prefer the exact search URL we came from (keeps filters and hits the search cache)
      const searchUrl = (location.state as { searchUrl?: string } | null)?.searchUrl
      const searchQuery = searchParams.get('q')
      if (searchUrl?.startsWith('/search')) {
        navigate(searchUrl)
      } else if (searchQuery) {
        navigate(`/search?query=${encodeURIComponent(searchQuery)}`)
      } else {
        navigate('/search')
      }
    } else if (fromParam === 'recently-viewed') {
      navigate('/recently-viewed')
    } else {
      if (file && file.folder_id && folderTree.length > 0) {
        const folder = findFolderById(folderTree, file.folder_id)
        if (folder) {
          const hash = folderPathToHash(folder.path)
          navigate(`/dashboard${hash}`)
          return
        }
      }
      navigate('/dashboard')
    }
  }

  if (isLoading) {
    return (
      <Center h="100%" bg="white">
        <Spinner size="xl" color="brand.500" />
      </Center>
    )
  }

  if (error || !file) {
    return (
      <Center h="100%" bg="white">
        <Alert status="error" borderRadius="lg" maxW="md">
          <AlertIcon />
          {error || t('filePreview.notFound', { ns: 'errors' })}
        </Alert>
      </Center>
    )
  }

  const handleFileRenamed = (updatedFile: FileItem) => {
    setFile(updatedFile)
    selectFile(updatedFile)
  }

  return <FilePreview file={file} onBack={handleBack} onFileRenamed={handleFileRenamed} />
}
